import React, { useState } from 'react'
import {
  CompanyId,
  CustomType,
  DeleteGoal$Request,
  GoalWithProgress,
  LeaveGoal$Request,
  Role,
} from '../../services/api.types'
import YumButton from '../YumButton/YumButton'
import { Capacitor } from '@capacitor/core'
import { jt, t } from 'ttag'
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core'
import { ButtonGroup } from '../ButtonGroup/ButtonGroup'
import { Button } from '../Button/Button'
import { darkGreen, primaryBlue, red } from '../../styles/material-ui-theme-v2'
import { IJoinGoalPayload } from '../../views/Activity/store/ActivitySagas'
import moment from 'moment/moment'
import { convertUTCToLocalDate } from '../../common/helpers/date'

import { ReactComponent as LogOutIcon } from '../../assets/v2/icons/log-out.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from '../../assets/icons/trash.svg'
import { ReactComponent as AddIcon } from '../../assets/v3/icons/add.svg'

export interface IYumChallengeTabsStateProps {
  className?: string
  goal: GoalWithProgress
  isListView?: boolean
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  joinInProgress: boolean
}

export interface IYumChallengeTabsDispatchProps {
  joinGoal(payload: IJoinGoalPayload): void
  leaveGoal(payload: LeaveGoal$Request): void
  deleteGoal(payload: DeleteGoal$Request): void
}

type IYumChallengeTabsProps = IYumChallengeTabsStateProps & IYumChallengeTabsDispatchProps

export default function YumChallengeActions(props: IYumChallengeTabsProps) {
  const { className, goal, isListView, hasRoleInCompany, joinGoal, leaveGoal, deleteGoal, joinInProgress } = props
  const {
    id: goalId,
    companyId,
    companyName,
    title,
    endDate,
    progressType,
    customType,
    participating,
    manualEntryEnabled,
  } = goal
  const challengeName = <strong key={'title'}>{title}</strong>

  const [contextMenuVisible, setContextMenuVisible] = useState(false)
  const [leaveGoalModalVisible, setLeaveGoalModalVisible] = useState(false)
  const [deleteGoalModalVisible, setDeleteGoalModalVisible] = useState(false)

  const isPresentOrFutureGoal = moment(convertUTCToLocalDate(new Date(endDate))).isAfter(new Date())
  const isUser = hasRoleInCompany(companyId, Role.USER)
  const isAdmin = hasRoleInCompany(companyId, Role.ADMIN)
  const actionsVisible = isAdmin || (isPresentOrFutureGoal && isUser)

  const onJoinActionClicked = (event?: any) => {
    if (event) event.preventDefault()
    setContextMenuVisible(false)

    const isPoints = goal.customType === CustomType.POINTS
    const pointsLogic = isPoints ? JSON.parse(goal.pointsLogic) : null
    const challengeStartDate = moment(convertUTCToLocalDate(new Date(goal.startDate)))
    const challengeEndDate = moment(convertUTCToLocalDate(new Date(goal.endDate)))
    joinGoal({
      goalId,
      progressType,
      customType,
      manualEntryEnabled,

      // For refreshChallengeResults.
      isPoints,
      pointsLogic,
      startDateFormatted: challengeStartDate.locale('en').format('YYYY-MM-DD'),
      endDateFormatted: challengeEndDate.locale('en').format('YYYY-MM-DD'),
    })
  }

  const toggleContextMenu = (event?: any) => {
    if (event) event.preventDefault()
    setContextMenuVisible(!contextMenuVisible)
  }
  const toggleLeaveGoalModal = () => setLeaveGoalModalVisible(!leaveGoalModalVisible)
  const toggleDeleteGoalModal = () => setDeleteGoalModalVisible(!deleteGoalModalVisible)

  const onLeaveGoalConfirmed = () => {
    toggleLeaveGoalModal()
    leaveGoal({ goalId })
  }

  const onLeaveGoalClicked = (event?: any) => {
    if (event) event.preventDefault()
    toggleContextMenu()
    toggleLeaveGoalModal()
  }

  const onDeleteGoalClicked = (event?: any) => {
    if (event) event.preventDefault()
    toggleContextMenu()
    toggleDeleteGoalModal()
  }

  const onDeleteGoalConfirmed = () => {
    toggleDeleteGoalModal()
    deleteGoal({ goalId })
  }

  const onBackdropClicked = (event: any) => {
    setLeaveGoalModalVisible(false)
    setDeleteGoalModalVisible(false)
  }

  const onContextMenuClicked = (event: any) => {
    event.preventDefault()

    if (Capacitor.getPlatform() === 'web') {
      return toggleContextMenu()
    }

    try {
      const showActions = async () => {
        const options = []
        if (participating) {
          options.push({
            title: t`Leave challenge`,
            style: isAdmin ? ActionSheetButtonStyle.Default : ActionSheetButtonStyle.Destructive,
          })
        } else if (isUser) {
          options.push({
            title: t`Join`,
            style: ActionSheetButtonStyle.Default,
          })
        }
        if (isAdmin) {
          options.push({
            title: t`Edit challenge`,
            style: ActionSheetButtonStyle.Default,
          })
        }
        if (isAdmin) {
          options.push({
            title: t`Delete challenge`,
            style: ActionSheetButtonStyle.Destructive,
          })
        }
        options.push({
          title: t`Cancel`,
          style: ActionSheetButtonStyle.Cancel,
        })
        const result = await ActionSheet.showActions({
          title: title,
          //message: companyName,
          options,
        })

        if (result) {
          if (participating && result.index === 0) {
            toggleLeaveGoalModal()
          }
          if (!participating && isUser && result.index === 0) {
            onJoinActionClicked()
          }
          if (
            isAdmin &&
            ((!participating && !isUser && result.index === 0) || ((participating || isUser) && result.index === 1))
          ) {
            openChallengeEditPage()
          }
          if (
            isAdmin &&
            ((!participating && !isUser && result.index === 1) || ((participating || isUser) && result.index === 2))
          ) {
            toggleDeleteGoalModal()
          }
        }
      }

      showActions()
    } catch (e) {
      return toggleContextMenu()
    }
  }

  const openChallengeEditPage = (event?: any) => {
    if (event) event.preventDefault()
    store.dispatch(push(`/challenges/${goalId}/edit`))
  }

  const showOnlyJoinButton = !participating && !isAdmin

  const preventRedirection = (event: any) => event.stopPropagation()

  return !actionsVisible ? (
    <></>
  ) : (
    <div className={className} onClick={preventRedirection}>
      {!showOnlyJoinButton && (
        <>
          <div className="yum-goal-card-context-menu">
            <ul
              className={`yum-goal-card-context-menu__dropdown ${
                contextMenuVisible ? 'yum-goal-card-context-menu__dropdown--open' : ''
              }`}
              style={!isListView ? { top: '30px' } : { top: 'auto', bottom: '10px' }}
            >
              {!participating && (
                <li>
                  <button
                    style={{ color: darkGreen }}
                    className="yum-goal-card-context-menu__link"
                    type="button"
                    onClick={onJoinActionClicked}
                  >
                    <AddIcon className="yum-goal-card-context-menu__icon" />
                    {t`Join`}
                  </button>
                </li>
              )}
              {participating && (
                <li>
                  <button className="yum-goal-card-context-menu__link" type="button" onClick={onLeaveGoalClicked}>
                    <LogOutIcon className="yum-goal-card-context-menu__icon" />
                    {t`Leave challenge`}
                  </button>
                </li>
              )}
              {isAdmin && (
                <>
                  <li>
                    <button className="yum-goal-card-context-menu__link" type="button" onClick={openChallengeEditPage}>
                      <EditIcon className="yum-goal-card-context-menu__icon" style={{ color: primaryBlue }} />
                      {t`Edit challenge`}
                    </button>
                  </li>
                  <li>
                    <button
                      className="yum-goal-card-context-menu__link"
                      type="button"
                      onClick={onDeleteGoalClicked}
                      style={{ color: red }}
                    >
                      <DeleteIcon className="yum-goal-card-context-menu__icon" />
                      {t`Delete challenge`}
                    </button>
                  </li>
                </>
              )}
            </ul>
            {contextMenuVisible && <div className="yum-goal-card-context-menu__backdrop" onClick={toggleContextMenu} />}
          </div>

          <Dialog open={leaveGoalModalVisible} onClose={onBackdropClicked}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t`Are you sure you want to leave this challenge?`}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <ButtonGroup>
                <Button text={t`Leave challenge`} onClick={onLeaveGoalConfirmed} />
                <Button text={t`Cancel`} link={true} onClick={toggleLeaveGoalModal} />
              </ButtonGroup>
            </DialogActions>
          </Dialog>

          {isAdmin && (
            <Dialog open={deleteGoalModalVisible} onClose={onBackdropClicked}>
              <DialogTitle>{title}</DialogTitle>
              <DialogContent style={{ maxWidth: 500 }}>
                <DialogContentText>
                  {jt`Are you sure you want to delete challenge ${challengeName} for everyone? Once deleted, you can't undo it.`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <ButtonGroup>
                  <Button text={t`Delete`} onClick={onDeleteGoalConfirmed} className="bg-red" />
                  <Button text={t`Cancel`} link={true} onClick={toggleDeleteGoalModal} />
                </ButtonGroup>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}

      {showOnlyJoinButton ? (
        <YumButton
          size={isListView ? 'small' : 'medium'}
          color="secondary"
          onClick={onJoinActionClicked}
          disabled={joinInProgress}
        >{t`Join`}</YumButton>
      ) : (
        <>
          {isListView ? (
            <div
              className="yum-goal-card__actions-button yum-goal-card__actions-button--context-menu"
              onClick={onContextMenuClicked}
            />
          ) : (
            <YumButton onClick={onContextMenuClicked} variant="text" size="medium" color="secondary">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15 5L8 12L1 5"
                  stroke="#006FFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </YumButton>
          )}
        </>
      )}
    </div>
  )
}
